import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge, STATUS_META, formatDate, DeadlineCell } from "@/lib/orderUtils";
import { clientColor } from "@/lib/clientColors";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Package, Clock, CheckCircle2, Users, Search, Hourglass, ArrowUp, ArrowDown, ChevronsUpDown, Zap } from "lucide-react";

function orderRequest(o) {
  const items = o.items || [];
  if (items.length) {
    return items
      .map((i) => (i.type || "") + (i.quantity > 1 ? ` \u00d7${i.quantity}` : ""))
      .filter(Boolean)
      .join(", ");
  }
  return o.design_type || o.work_type || "";
}

export default function AdminDashboard({ view = "active" }) {
  const isCompletedView = view === "completed";
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, ricevuto: 0, presa_in_carico: 0, in_lavorazione: 0, completato: 0, clients: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [sortField, setSortField] = useState("deadline");
  const [sortDir, setSortDir] = useState("asc");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "deadline" ? "asc" : "desc");
    }
  };

  const operatorTouched = (o) =>
    o.operator_touched === true ||
    (o.files || []).some((f) => f.uploaded_by_role === "operator" || f.last_downloaded_role === "operator");

  const load = async () => {
    try {
      const [o, s] = await Promise.all([api.get(`/orders?view=${view}`), api.get("/admin/stats")]);
      setOrders(o.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento stato");
    }
  };

  const filtered = useMemo(() => {
    const arr = orders.filter((o) => {
      const matchQ =
        !q ||
        o.patient_name?.toLowerCase().includes(q.toLowerCase()) ||
        o.client_name?.toLowerCase().includes(q.toLowerCase()) ||
        o.order_number?.toLowerCase().includes(q.toLowerCase());
      const matchF = filter === "tutti" || o.status === filter;
      return matchQ && matchF;
    });
    arr.sort((a, b) => {
      if (sortField === "deadline") {
        const da = a.deadline || "9999-12-31";
        const db = b.deadline || "9999-12-31";
        const cmp = da < db ? -1 : da > db ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      }
      const ca = a.created_at || "";
      const cb = b.created_at || "";
      const cmp = ca < cb ? -1 : ca > cb ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [orders, q, filter, sortField, sortDir]);

  return (
    <DashboardLayout
      title={isCompletedView ? "Ordini completati" : "Pannello laboratorio"}
      subtitle={isCompletedView ? "Archivio degli ordini completati. Se il cliente invia un aggiornamento, l'ordine torna automaticamente in \u201cOrdini\u201d." : "Gestisci tutti gli ordini dei clienti."}
    >
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={Package} label="Totali" value={stats.total} color="text-teal-600" />
        <StatCard icon={Clock} label="Ricevuti" value={stats.ricevuto} color="text-slate-500" />
        <StatCard icon={Hourglass} label="Presa in carico" value={stats.presa_in_carico} color="text-sky-600" />
        <StatCard icon={Loader2} label="In lavorazione" value={stats.in_lavorazione} color="text-amber-600" />
        <StatCard icon={CheckCircle2} label="Completati" value={stats.completato} color="text-emerald-600" />
        <StatCard icon={Users} label="Clienti" value={stats.clients} color="text-indigo-600" />
      </div>

      <Card className="border-slate-200">
        <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-slate-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per paziente, cliente o numero..." className="pl-9" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-status-filter" className={`sm:w-48 ${isCompletedView ? "hidden" : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli stati</SelectItem>
              <SelectItem value="ricevuto">Ricevuto</SelectItem>
              <SelectItem value="presa_in_carico">Presa in carico</SelectItem>
              <SelectItem value="in_attesa_scomposizione_barra">In attesa di scomposizione barra</SelectItem>
              <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div data-testid="operator-legend" className="px-4 py-2 flex items-center gap-2 text-xs text-slate-500 border-b border-slate-100">
            <span className="inline-block h-3 w-3 rounded-sm bg-amber-50 border border-amber-300" />
            Riga in ambra = caso scaricato o lavorato dal collega
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
        ) : (
          <Table data-testid="admin-orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>Ordine</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Paziente</TableHead>
                <TableHead>Lavoro</TableHead>
                {isAdmin && <TableHead>Richiesta</TableHead>}
                <TableHead><SortHeader label="Inserito" field="created_at" sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="sort-inserito" /></TableHead>
                <TableHead><SortHeader label="Scadenza" field="deadline" sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="sort-scadenza" /></TableHead>
                <TableHead>Quantità</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12 text-slate-400">Nessun ordine trovato</TableCell></TableRow>
              ) : (
                filtered.map((o) => (
                  <TableRow key={o.id} data-testid="admin-order-row" className={`hover:bg-slate-50 cursor-pointer ${isAdmin && operatorTouched(o) ? "bg-amber-50/70 hover:bg-amber-100/70" : ""}`}>
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)} className="font-mono text-xs font-medium text-teal-600">
                      <span className="flex items-center gap-1.5">
                        {isAdmin && o.shared_with_operator && <span data-testid="shared-dot" title="Condiviso con il collega" className="inline-block h-2 w-2 rounded-full bg-teal-500 shrink-0" />}
                        {o.has_client_update && <span data-testid="client-update-badge" title="Aggiornamento post-prova dal cliente" className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-600 text-white text-xs font-bold shrink-0 animate-pulse">!</span>}
                        {o.urgency_requested && <span data-testid="urgent-badge" title="Urgenza richiesta dal cliente" className="inline-flex items-center gap-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 shrink-0"><Zap className="h-3 w-3" />Urgente</span>}
                        {isAdmin && o.billing_month && <span data-testid="billing-shift-badge" title={`Spostato contabilmente a ${o.billing_month}`} className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.5 shrink-0">→ {["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"][parseInt(o.billing_month.slice(5, 7), 10) - 1]}</span>}
                        {o.order_number}
                      </span>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)} className="relative" style={clientColor(o.client_color) ? { backgroundColor: clientColor(o.client_color).bg, boxShadow: `inset 4px 0 0 ${clientColor(o.client_color).border}` } : undefined} data-testid="order-client-cell">
                      <div className="font-medium text-slate-800">{o.client_name}</div>
                      <div className="text-xs text-slate-500">{o.client_company || o.client_email}</div>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)}>{o.patient_name}</TableCell>
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)}>
                      <div className="text-slate-800">{o.macro_category || o.work_type}</div>
                      {o.material && <div className="text-xs text-slate-400">{o.material}{o.color ? ` · ${o.color}` : ""}</div>}
                    </TableCell>
                    {isAdmin && (
                      <TableCell onClick={() => navigate(`/orders/${o.id}`)} className="max-w-[220px]">
                        <span className="text-sm font-medium text-slate-700 whitespace-normal break-words">{orderRequest(o) || "—"}</span>
                      </TableCell>
                    )}
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)} className="text-sm text-slate-500">{formatDate(o.created_at)}</TableCell>
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)} className="text-sm text-slate-500"><DeadlineCell order={o} /></TableCell>
                    <TableCell onClick={() => navigate(`/orders/${o.id}`)} className="text-sm font-medium text-slate-700">{o.quantity || "—"}</TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger data-testid="admin-status-select" className="h-8 w-40 border-0 bg-transparent p-0 focus:ring-0">
                          <StatusBadge status={o.status} />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(STATUS_META).map((s) => (
                            <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </DashboardLayout>
  );
}

function SortHeader({ label, field, sortField, sortDir, onClick, testid }) {
  const active = sortField === field;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onClick(field)}
      className={`inline-flex items-center gap-1 select-none transition-colors ${active ? "text-teal-600" : "text-slate-500 hover:text-slate-800"}`}
    >
      {label}
      {active ? (sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />}
    </button>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="p-5 border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
            <p className="font-heading text-2xl font-semibold mt-1 text-slate-900">{value}</p>
          </div>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </Card>
    </motion.div>
  );
}
